import React, { useCallback, useEffect, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist";
import "./VisorPDF.css";

pdfjsLib.GlobalWorkerOptions.workerSrc = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js";

function base64ToBlob(base64, mimeType = "application/pdf") {
  if (!base64) throw new Error("No se recibió contenido Base64.");
  let contenido = base64;
  if (contenido.startsWith("data:")) {
    const coma = contenido.indexOf(",");
    if (coma === -1) throw new Error("El Base64 recibido no tiene un formato válido.");
    contenido = contenido.substring(coma + 1);
  }
  contenido = contenido.replace(/\s/g, "");
  const binaryString = window.atob(contenido);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) bytes[i] = binaryString.charCodeAt(i);
  return new Blob([bytes], { type: mimeType });
}

export default function VisorPDF({
  id = "mi-pdf",
  titulo = "PDF",
  base64,
  nombreArchivo = "documento.pdf",
  autoDownload = true,
  modoImpresion = "ventana",
  alto = "100%",
}) {
  const viewportRef = useRef(null);
  const renderTargetRef = useRef(null);
  const pdfRef = useRef(null);
  const blobRef = useRef(null);
  const blobUrlRef = useRef(null);
  const renderTokenRef = useRef(0);
  const pageSizesRef = useRef([]);
  const zoomRef = useRef(1);
  const pointersRef = useRef(new Map());

  const gestureRef = useRef({
    type: "none",
    startX: 0,
    startY: 0,
    startScrollLeft: 0,
    startScrollTop: 0,
    initialDistance: 0,
    initialZoom: 1,
    moved: false,
  });

  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [zoomInput, setZoomInput] = useState("100");

  const limitarZoom = (valor) => {
    return Math.min(Math.max(valor, 0.4), 4);
  };

  const actualizarTamanoCanvas = useCallback((nuevoZoom) => {
    const renderTarget = renderTargetRef.current;

    if (!renderTarget) return;

    renderTarget.querySelectorAll(".pdf-canvas").forEach((canvas, index) => {
      const pageSize = pageSizesRef.current[index];

      if (!pageSize) return;

      const nuevoAncho = pageSize.width * nuevoZoom;
      const nuevoAlto = pageSize.height * nuevoZoom;

      canvas.style.width = `${nuevoAncho}px`;
      canvas.style.height = `${nuevoAlto}px`;
    });
  }, []);

  const aplicarZoom = useCallback(
    (nuevoZoom, foco = null) => {
      const viewport = viewportRef.current;

      if (!viewport) return;

      nuevoZoom = limitarZoom(nuevoZoom);

      const zoomAnterior = zoomRef.current;

      if (Math.abs(nuevoZoom - zoomAnterior) < 0.0001) return;

      if (!foco) {
        foco = {
          x: viewport.clientWidth / 2,
          y: viewport.clientHeight / 2,
        };
      }

      const documentoX = viewport.scrollLeft + foco.x;
      const documentoY = viewport.scrollTop + foco.y;

      const ratio = nuevoZoom / zoomAnterior;

      zoomRef.current = nuevoZoom;

      actualizarTamanoCanvas(nuevoZoom);

      setZoom(nuevoZoom);

      setZoomInput(String(Math.round(nuevoZoom * 100)));

      requestAnimationFrame(() => {
        const v = viewportRef.current;

        if (!v) return;

        v.scrollLeft = documentoX * ratio - foco.x;
        v.scrollTop = documentoY * ratio - foco.y;
      });
    },
    [actualizarTamanoCanvas],
  );

  useEffect(() => {
    let cancelado = false;

    const token = ++renderTokenRef.current;

    const cargarPDF = async () => {
      try {
        setCargando(true);
        setError(null);

        if (!base64) {
          setError("No se recibió el PDF.");
          setCargando(false);

          return;
        }

        if (pdfRef.current) {
          try {
            await pdfRef.current.destroy();
          } catch {}

          pdfRef.current = null;
        }

        if (blobUrlRef.current) {
          URL.revokeObjectURL(blobUrlRef.current);

          blobUrlRef.current = null;
        }

        const renderTarget = renderTargetRef.current;

        if (renderTarget) {
          while (renderTarget.firstChild) {
            renderTarget.removeChild(renderTarget.firstChild);
          }
        }

        pageSizesRef.current = [];

        zoomRef.current = 1;

        setZoom(1);
        setZoomInput("100");

        const blob = base64ToBlob(base64, "application/pdf");

        if (cancelado || token !== renderTokenRef.current) return;

        blobRef.current = blob;

        const blobUrl = URL.createObjectURL(blob);

        blobUrlRef.current = blobUrl;

        const loadingTask = pdfjsLib.getDocument({
          url: blobUrl,
        });

        const pdf = await loadingTask.promise;

        if (cancelado || token !== renderTokenRef.current) {
          try {
            await pdf.destroy();
          } catch {}

          return;
        }

        pdfRef.current = pdf;

        const renderScale = 2;

        for (let numeroPagina = 1; numeroPagina <= pdf.numPages; numeroPagina++) {
          if (cancelado || token !== renderTokenRef.current) break;

          const page = await pdf.getPage(numeroPagina);

          if (cancelado || token !== renderTokenRef.current) break;

          const pdfViewport = page.getViewport({
            scale: renderScale,
          });

          const canvas = document.createElement("canvas");

          canvas.className = "pdf-canvas";

          canvas.width = Math.ceil(pdfViewport.width);

          canvas.height = Math.ceil(pdfViewport.height);

          const baseWidth = pdfViewport.width / renderScale;

          const baseHeight = pdfViewport.height / renderScale;

          pageSizesRef.current.push({
            width: baseWidth,
            height: baseHeight,
          });

          canvas.style.width = `${baseWidth}px`;

          canvas.style.height = `${baseHeight}px`;

          if (renderTargetRef.current) {
            renderTargetRef.current.appendChild(canvas);
          }

          const context = canvas.getContext("2d", {
            alpha: false,
          });

          await page.render({
            canvasContext: context,
            viewport: pdfViewport,
          }).promise;
        }

        if (!cancelado && token === renderTokenRef.current) {
          setCargando(false);
        }
      } catch (e) {
        if (cancelado || token !== renderTokenRef.current) return;

        console.error("Error cargando PDF:", e);

        setError("No se pudo cargar el PDF.");

        setCargando(false);
      }
    };

    cargarPDF();

    return () => {
      cancelado = true;

      renderTokenRef.current++;

      const pdf = pdfRef.current;

      pdfRef.current = null;

      if (pdf) {
        try {
          pdf.destroy();
        } catch {}
      }

      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);

        blobUrlRef.current = null;
      }

      blobRef.current = null;
    };
  }, [base64]);

  const descargarPDF = async () => {
    try {
      const blob = blobRef.current;

      if (!blob) {
        console.error("No existe PDF para descargar.");

        return;
      }

      if (autoDownload === false && "showSaveFilePicker" in window) {
        try {
          const handle = await window.showSaveFilePicker({
            suggestedName: nombreArchivo.endsWith(".pdf") ? nombreArchivo : `${nombreArchivo}.pdf`,

            types: [
              {
                description: "Archivo PDF",
                accept: {
                  "application/pdf": [".pdf"],
                },
              },
            ],
          });

          const writable = await handle.createWritable();

          await writable.write(blob);

          await writable.close();

          return;
        } catch (error) {
          if (error?.name === "AbortError") return;

          console.warn("No se pudo utilizar el selector de archivos.", error);
        }
      }

      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = url;

      link.download = nombreArchivo.endsWith(".pdf") ? nombreArchivo : `${nombreArchivo}.pdf`;

      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);

      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 1000);
    } catch (error) {
      console.error("Error descargando PDF:", error);
    }
  };

  const imprimirPDF = () => {
    try {
      const blob = blobRef.current;

      if (!blob) {
        console.error("No existe PDF para imprimir.");

        return;
      }

      const url = URL.createObjectURL(blob);

      if (modoImpresion === "sistema") {
        const iframe = document.createElement("iframe");

        iframe.style.position = "fixed";
        iframe.style.right = "0";
        iframe.style.bottom = "0";
        iframe.style.width = "0";
        iframe.style.height = "0";
        iframe.style.border = "0";
        iframe.style.opacity = "0";

        iframe.src = url;

        document.body.appendChild(iframe);

        iframe.onload = () => {
          try {
            setTimeout(() => {
              iframe.contentWindow.focus();
              iframe.contentWindow.print();
            }, 300);
          } catch (error) {
            console.error("No se pudo abrir el diálogo de impresión.", error);
          }

          setTimeout(() => {
            try {
              document.body.removeChild(iframe);
            } catch {}

            URL.revokeObjectURL(url);
          }, 60000);
        };

        return;
      }

      const ventana = window.open(url, "_blank", "noopener,noreferrer");

      if (!ventana) {
        const link = document.createElement("a");

        link.href = url;
        link.target = "_blank";
        link.rel = "noopener noreferrer";

        document.body.appendChild(link);

        link.click();

        document.body.removeChild(link);
      }

      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 60000);
    } catch (error) {
      console.error("Error abriendo PDF para impresión:", error);
    }
  };

  useEffect(() => {
    const viewport = viewportRef.current;

    if (!viewport) return;

    const wheel = (event) => {
      if (!event.ctrlKey) return;

      event.preventDefault();

      const rect = viewport.getBoundingClientRect();

      const foco = {
        x: event.clientX - rect.left,

        y: event.clientY - rect.top,
      };

      const factor = event.deltaY < 0 ? 1.12 : 0.88;

      aplicarZoom(zoomRef.current * factor, foco);
    };

    viewport.addEventListener("wheel", wheel, { passive: false });

    return () => {
      viewport.removeEventListener("wheel", wheel);
    };
  }, [aplicarZoom]);

  useEffect(() => {
    const viewport = viewportRef.current;

    if (!viewport) return;

    const pointerDown = (event) => {
      try {
        viewport.setPointerCapture(event.pointerId);
      } catch {}

      pointersRef.current.set(event.pointerId, {
        clientX: event.clientX,
        clientY: event.clientY,
      });

      const pointers = Array.from(pointersRef.current.values());

      if (pointers.length === 2) {
        const [p1, p2] = pointers;

        const distancia = Math.hypot(p1.clientX - p2.clientX, p1.clientY - p2.clientY);

        gestureRef.current = {
          type: "pinch",

          initialDistance: distancia,

          initialZoom: zoomRef.current,

          startX: 0,
          startY: 0,
          startScrollLeft: 0,
          startScrollTop: 0,

          moved: false,
        };

        event.preventDefault();

        return;
      }

      if (pointers.length === 1) {
        gestureRef.current = {
          type: "pan",

          startX: event.clientX,

          startY: event.clientY,

          startScrollLeft: viewport.scrollLeft,

          startScrollTop: viewport.scrollTop,

          initialDistance: 0,

          initialZoom: zoomRef.current,

          moved: false,
        };
      }
    };

    const pointerMove = (event) => {
      if (!pointersRef.current.has(event.pointerId)) return;

      pointersRef.current.set(event.pointerId, {
        clientX: event.clientX,
        clientY: event.clientY,
      });

      const pointers = Array.from(pointersRef.current.values());

      if (pointers.length === 2 && gestureRef.current.type === "pinch") {
        event.preventDefault();

        const [p1, p2] = pointers;

        const distancia = Math.hypot(p1.clientX - p2.clientX, p1.clientY - p2.clientY);

        const distanciaInicial = gestureRef.current.initialDistance;

        if (!distanciaInicial) return;

        const factor = distancia / distanciaInicial;

        const nuevoZoom = limitarZoom(gestureRef.current.initialZoom * factor);

        const centroX = (p1.clientX + p2.clientX) / 2;

        const centroY = (p1.clientY + p2.clientY) / 2;

        const rect = viewport.getBoundingClientRect();

        const foco = {
          x: centroX - rect.left,

          y: centroY - rect.top,
        };

        aplicarZoom(nuevoZoom, foco);

        gestureRef.current.moved = true;

        return;
      }

      if (pointers.length === 1 && gestureRef.current.type === "pan") {
        event.preventDefault();

        const deltaX = event.clientX - gestureRef.current.startX;

        const deltaY = event.clientY - gestureRef.current.startY;

        viewport.scrollLeft = gestureRef.current.startScrollLeft - deltaX;

        viewport.scrollTop = gestureRef.current.startScrollTop - deltaY;

        gestureRef.current.moved = true;
      }
    };

    const pointerEnd = (event) => {
      pointersRef.current.delete(event.pointerId);

      const pointers = Array.from(pointersRef.current.values());

      if (pointers.length === 1) {
        const pointer = pointers[0];

        gestureRef.current = {
          type: "pan",

          startX: pointer.clientX,

          startY: pointer.clientY,

          startScrollLeft: viewport.scrollLeft,

          startScrollTop: viewport.scrollTop,

          initialDistance: 0,

          initialZoom: zoomRef.current,

          moved: true,
        };
      } else if (pointers.length === 0) {
        gestureRef.current.type = "none";
      }

      try {
        viewport.releasePointerCapture(event.pointerId);
      } catch {}
    };

    viewport.addEventListener("pointerdown", pointerDown, { passive: false });

    viewport.addEventListener("pointermove", pointerMove, { passive: false });

    viewport.addEventListener("pointerup", pointerEnd);

    viewport.addEventListener("pointercancel", pointerEnd);

    return () => {
      viewport.removeEventListener("pointerdown", pointerDown);

      viewport.removeEventListener("pointermove", pointerMove);

      viewport.removeEventListener("pointerup", pointerEnd);

      viewport.removeEventListener("pointercancel", pointerEnd);

      pointersRef.current.clear();
    };
  }, [aplicarZoom]);

  const cambiarZoomInput = (event) => {
    const valor = event.target.value;

    if (!/^\d*$/.test(valor)) return;

    setZoomInput(valor);

    if (event.key !== "Enter") return;

    const numero = parseInt(valor, 10);

    if (Number.isNaN(numero)) {
      setZoomInput(String(Math.round(zoomRef.current * 100)));

      return;
    }

    aplicarZoom(numero / 100);

    event.target.blur();
  };

  const aplicarZoomInput = () => {
    const numero = parseInt(zoomInput, 10);

    if (Number.isNaN(numero)) {
      setZoomInput(String(Math.round(zoomRef.current * 100)));

      return;
    }

    aplicarZoom(numero / 100);
  };

  return (
    <div id={id} className="visor-pdf" style={{ height: alto }}>
      <div className="pdf-toolbar">
        <div className="pdf-title">{titulo}</div>

        <div className="pdf-controls">
          <button type="button" className="pdf-btn" title="Alejar" onClick={() => aplicarZoom(zoomRef.current - 0.15)}>
            −
          </button>

          <div className="pdf-zoom-box">
            <input
              type="text"
              inputMode="numeric"
              className="pdf-zoom-input"
              value={zoomInput}
              onChange={(event) => {
                const valor = event.target.value;

                if (/^\d*$/.test(valor)) {
                  setZoomInput(valor);
                }
              }}
              onKeyDown={cambiarZoomInput}
              onBlur={aplicarZoomInput}
              onFocus={(event) => event.target.select()}
            />

            <span className="pdf-zoom-percent">%</span>
          </div>

          <button type="button" className="pdf-btn" title="Acercar" onClick={() => aplicarZoom(zoomRef.current + 0.15)}>
            +
          </button>
        </div>

        <div className="pdf-toolbar-actions">
          <button type="button" className="pdf-btn" title="Imprimir PDF" onClick={imprimirPDF}>
            🖨
          </button>

          <button type="button" className="pdf-btn" title="Descargar PDF" onClick={descargarPDF}>
            ⤓
          </button>
        </div>
      </div>

      <div ref={viewportRef} className="pdf-viewport">
        <div ref={renderTargetRef} className="pdf-content-area" />

        {cargando && <div className="visor-pdf-loading">Cargando PDF...</div>}

        {error && <div className="visor-pdf-error">{error}</div>}
      </div>
    </div>
  );
}

/*
<VisorPDF
    id="mi-pdf"
    titulo="PDF"
    base64={v_u.d_pdf}
    nombreArchivo="archivo san miguel 12-12-12"
/>

<VisorPDF
    id="mi-pdf"
    titulo="PDF"
    base64={v_u.d_pdf}
    nombreArchivo="archivo san miguel 12-12-12.pdf"
    autoDownload={false}
/>

<VisorPDF
    id="mi-pdf"
    titulo="PDF"
    base64={v_u.d_pdf}
    nombreArchivo="archivo san miguel 12-12-12.pdf"
    autoDownload={true}
    modoImpresion="sistema"
/>

<VisorPDF
    id="mi-pdf"
    titulo="PDF"
    base64={v_u.d_pdf}
    nombreArchivo="archivo san miguel 12-12-12.pdf"
    modoImpresion="ventana"
/>

*/
